import { useCallback, useRef, useState } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { expValEquals } from '@confluence/feature-experiments';

import { APPROVALS_ANALYTICS_SOURCE } from './analytics.ts';

type UseLiveDocConversionBlockedModalArgs = {
	contentId: string;
	contentType: string | null;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	disallowFirstPublish: boolean | undefined;
	isLivePage: boolean;
};

export const useLiveDocConversionBlockedModal = ({
	contentId,
	contentType,
	createAnalyticsEvent,
	disallowFirstPublish,
	isLivePage,
}: UseLiveDocConversionBlockedModalArgs): {
	handleCloseLiveDocConversionBlockedModal: () => void;
	handleLiveDocConversionBlockedByApproval: () => void;
	isLiveDocConversionBlockedModalOpen: boolean;
	shouldBlockLiveDocConversion: boolean;
} => {
	const [isLiveDocConversionBlockedModalOpen, setIsLiveDocConversionBlockedModalOpen] =
		useState(false);
	const isLiveDocConversionBlockedModalOpenRef = useRef(false);

	const shouldBlockLiveDocConversion =
		isLivePage &&
		disallowFirstPublish === true &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true);

	const handleCloseLiveDocConversionBlockedModal = useCallback((): void => {
		isLiveDocConversionBlockedModalOpenRef.current = false;
		setIsLiveDocConversionBlockedModalOpen(false);
	}, []);

	const handleLiveDocConversionBlockedByApproval = useCallback((): void => {
		if (!isLiveDocConversionBlockedModalOpenRef.current) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'liveDocConversionBlockedScreen',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: {
						contentId,
						contentType,
						isLivePage,
						conversionSource: 'liveDocToPage',
					},
				},
			}).fire();
		}

		isLiveDocConversionBlockedModalOpenRef.current = true;
		setIsLiveDocConversionBlockedModalOpen(true);
	}, [contentId, contentType, createAnalyticsEvent, isLivePage]);

	return {
		handleCloseLiveDocConversionBlockedModal,
		handleLiveDocConversionBlockedByApproval,
		isLiveDocConversionBlockedModalOpen,
		shouldBlockLiveDocConversion,
	};
};
